/**
 * Tool-visibility flags for chat (issue #1332).
 *
 * Exposes the workspace-level "show tool calls" / "show tool output" settings
 * to chat renderers via a module-level store, so every consumer — the deeply
 * nested message renderers AND the top-level Chat side panel — reads the same
 * resolved values without any provider-placement concerns.
 *
 * The flags are display-only and workspace-scoped, fetched once per session and
 * cached. Until they resolve, `resolved` is false and consumers MUST hide tool
 * activity/output, so a workspace that disabled tools never flashes raw tool
 * calls or error output on first paint (the safe direction). Subsequent chat
 * mounts read the cached value synchronously, so there is no flash or delay.
 */

import { useSyncExternalStore } from 'react'

import { workspaceService } from '@/services/workspace-service'

export interface ToolVisibility {
  /** Whether tool-call activity is rendered in chat at all. */
  showToolCalls: boolean
  /**
   * Whether tool output/results are rendered. Always false when calls are
   * hidden, so consumers never have to special-case the dependency.
   */
  showToolOutput: boolean
  /**
   * True once the workspace settings have loaded. While false, consumers hide
   * tool activity/output rather than assume full visibility.
   */
  resolved: boolean
}

/**
 * Module-level cache shared across every hook consumer. Defaults to full
 * visibility but unresolved, so the first paint hides tool activity until the
 * authoritative settings arrive.
 */
let state: ToolVisibility = {
  showToolCalls: true,
  showToolOutput: true,
  resolved: false,
}

const listeners = new Set<() => void>()
let fetchStarted = false

function emit() {
  for (const listener of listeners) listener()
}

/** Fetches the workspace settings once and publishes them to all consumers. */
function ensureFetch() {
  if (fetchStarted) return
  fetchStarted = true
  workspaceService
    .getSuperAgentSettings()
    .then((settings) => {
      state = {
        showToolCalls: settings.showToolCalls,
        showToolOutput: settings.showToolCalls && settings.showToolOutput,
        resolved: true,
      }
    })
    .catch(() => {
      // Resolve to the visible defaults on failure — never leave the chat
      // permanently gated because a settings fetch failed.
      state = { showToolCalls: true, showToolOutput: true, resolved: true }
    })
    .finally(emit)
}

/**
 * Clears the cached flags and refetches. Call after the workspace tool-
 * visibility settings change so open chats pick up the new values on their
 * next render without a full page reload.
 */
export function invalidateToolVisibility() {
  fetchStarted = false
  state = { ...state, resolved: false }
  emit()
  ensureFetch()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  ensureFetch()
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): ToolVisibility {
  return state
}

/** Reads the current workspace tool-visibility flags. */
export function useToolVisibility(): ToolVisibility {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
